import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { Breadcrumbs, P } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MENUITEMS } from "../../../Layout/Sidebar/Menu";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  getAllRoles,
  getPermissions,
  createPermissions,
  deletePermissions,
} from "../../../store/slices/usersSlice";
import CreateRoleModal from "./createRoleModal";
import Swal from "sweetalert2";
import { useDebounce } from "../../../Hooks/useDebounce";

const PermissionTable = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedActions, setSelectedActions] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterSearch = useDebounce(columnFilters, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const permissionsData = useSelector((state) => state.users.permissionsData);

  const rolesData = useSelector((state) => state.users.roles);
  const [availableActions, setAvailableActions] = useState([]);
  const handleModuleChange = (e) => {
    const moduleTitle = e.target.value;
    setSelectedModule(moduleTitle);

    const matchedItem = MENUITEMS.flatMap((section) => section.Items).find(
      (item) => item.title === moduleTitle,
    );

    setAvailableActions(matchedItem?.actions?.filter(Boolean) || []);
    setSelectedActions([]);
  };

  const handleActionChange = (action) => {
    setSelectedActions((prev) =>
      prev.includes(action)
        ? prev.filter((a) => a !== action)
        : [...prev, action],
    );
  };

  const handleAdd = async () => {
    await dispatch(
      createPermissions({
        permission_key: selectedModule,
        permission_value: selectedActions,
      }),
    );
    toggle();
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  };

  const fetchData = async (payload) => {
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder =
      sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;

    await dispatch(
      getPermissions({
        pagination: true,
        limit: pageSize,
        offset: page * pageSize,
        sortBy: sortField,
        sortOrder: sortOrder,
        ...payload,
      }),
    );
  };

  useEffect(() => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  }, [dispatch, page, pageSize, debouncedSearch, columnFilterSearch, sorting]);

  const handlePageChange = (event) => {
    const newState =
      typeof event === "function"
        ? event({ pageIndex: page, pageSize: pageSize })
        : event;

    setPage(newState.pageIndex);
    setPageSize(newState.pageSize);
  };

  const handleOnChangeSearch = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "permission_key",
        header: "Module",
        size: 50,
      },

      {
        accessorKey: "permission_value",
        header: "Action",
        size: 50,
      },

      {
        accessorKey: "delete",
        header: "Delete",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className="btn btn-danger"
            onClick={async () => {
              Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
              }).then(async (result) => {
                if (result.isConfirmed) {
                  await dispatch(deletePermissions(row.original.ID));
                  fetchData({
                    searchQuery: debouncedSearch,
                    filters: columnFilterSearch,
                  });
                }
              });
            }}
          >
            Delete
          </button>
        ),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: permissionsData?.permissionsData || [],
    initialState: { density: "compact" },
    globalFilter: searchQuery,
    onGlobalFilterChange: handleOnChangeSearch,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: permissionsData?.count,
    onPaginationChange: handlePageChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      sorting,
      columnFilters,
      globalFilter: searchQuery,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <button className="btn bg-primary" onClick={() => setModalOpen(true)}>
          Create
        </button>
      </div>
    ),
  });

  const toggle = useCallback(() => {
    setSelectedModule("");
    setSelectedActions([]);
    setModalOpen(!isModalOpen);
  }, [isModalOpen]);

  useEffect(() => {
    console.log(permissionsData, "permissionsData.data");
  }, [permissionsData]);

  return (
    <Fragment>
      {/* <CreateRoleModal isOpen={isModalOpen} toggle={toggle}></CreateRoleModal> */}
      <Modal isOpen={isModalOpen} toggle={toggle} className="permissions-modal">
        <ModalBody>
          <h5>Select Module</h5>
          <select
            className="form-control mb-3"
            value={selectedModule}
            onChange={handleModuleChange}
          >
            <option value="">-- Select Module --</option>
            {MENUITEMS.flatMap((section) => section.Items).map((item) => (
              <option key={item.title} value={item.title}>
                {item.title}
              </option>
            ))}
          </select>

          <h5>Select Actions</h5>
          {availableActions.map((action) => (
            <div className="form-check" key={action}>
              <input
                // className="form-check-input"
                type="checkbox"
                id={action}
                checked={selectedActions.includes(action)}
                onChange={() => handleActionChange(action)}
                className="peer h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <label className="form-check-label" htmlFor={action}>
                {action}
              </label>
            </div>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              console.log("Module:", selectedModule);
              console.log("Actions:", selectedActions);
              handleAdd();
            }}
            disabled={!selectedModule || selectedActions.length === 0}
          >
            Save
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Breadcrumbs
        mainTitle="Permission Management"
        parent="Permission"
        parentPath="permission-management"
        title="Permission List"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PermissionTable;
